// pages/data.jsx
import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';

// API Configuration
const API_URL = 'https://script.google.com/macros/s/AKfycbw4IxMhBX2yAbaW7cjOYqmmlmy6XqjNcWy1pNBkWY6YyMibdvgYIa1OqwMttNY4nfEQyw/exec';

// Create Context for Data
const DataContext = createContext();

// Custom hook to use data context
export const useData = () => useContext(DataContext);

// Main Data Provider Component
export const DataProvider = ({ children }) => {
  const [allData, setAllData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setErrorMsg(null);
    
    try {
      const response = await fetch(`${API_URL}?type=all`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const data = await response.json();
      
      // Process and organize data
      const processedData = processApiData(data);
      
      setAllData(processedData);
      setLastUpdated(new Date().toISOString());
      
      // Save to localStorage
      localStorage.setItem('telegramUserData', JSON.stringify({
        data: processedData,
        lastUpdated: new Date().toISOString()
      }));
      
    } catch (err) {
      setErrorMsg(err.message);
      // Try to load from cache if available
      const cached = localStorage.getItem('telegramUserData');
      if (cached) {
        const { data: cachedData, lastUpdated: cachedTime } = JSON.parse(cached);
        setAllData(cachedData);
        setLastUpdated(cachedTime);
        setErrorMsg(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Process API data into organized structure
  const processApiData = (apiData) => {
    const usersMap = new Map();
    const groupsMap = new Map();
    
    // Process users
    if (apiData.users && Array.isArray(apiData.users)) {
      apiData.users.forEach(user => {
        usersMap.set(user.userid, {
          ...user,
          groupsList: Object.values(user.groups || {})
        });
      });
    }
    
    // Process groups
    if (apiData.groups && Array.isArray(apiData.groups)) {
      apiData.groups.forEach(group => {
        groupsMap.set(group.groupId, {
          ...group,
          users: group.members || []
        });
      });
    }
    
    return {
      users: Array.from(usersMap.values()),
      groups: Array.from(groupsMap.values()),
      summary: apiData.summary || {
        totalUsers: 0,
        totalGroups: 0,
        averageGroupsPerUser: 0,
        averageMembersPerGroup: 0
      },
      raw: apiData
    };
  };

  // Manual refresh
  const refreshData = () => {
    fetchData();
  };

  // Load from cache only on mount (no auto-fetch)
  useEffect(() => {
    const cached = localStorage.getItem('telegramUserData');
    if (cached) {
      const { data: cachedData, lastUpdated: cachedTime } = JSON.parse(cached);
      setAllData(cachedData);
      setLastUpdated(cachedTime);
    }
    // DO NOT auto-fetch - wait for user to click refresh
  }, []);

  return (
    <DataContext.Provider value={{
      allData,
      loading,
      error: errorMsg,
      lastUpdated,
      refreshData
    }}>
      {children}
    </DataContext.Provider>
  );
};

// Search Bar Component
const SearchBar = ({ onSearch }) => {
  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const handleSearch = () => {
    onSearch(query, filterType);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="search-bar">
      <input
        type="text"
        placeholder="Search users or groups..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyPress={handleKeyPress}
      />
      <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
        <option value="all">All</option>
        <option value="users">Users Only</option>
        <option value="groups">Groups Only</option>
      </select>
      <button onClick={handleSearch}>Search</button>
    </div>
  );
};

// Top Groups Component
const TopGroups = ({ groups, onGroupClick }) => {
  const top20Groups = [...groups].sort((a, b) => b.memberCount - a.memberCount).slice(0, 20);
  
  if (top20Groups.length === 0) {
    return (
      <div className="top-groups">
        <h2>🏆 Top 20 Groups</h2>
        <p>No groups found</p>
      </div>
    );
  }
  
  return (
    <div className="top-groups">
      <h2>🏆 Top 20 Groups (by member count)</h2>
      <div className="groups-grid">
        {top20Groups.map(group => (
          <div key={group.groupId} className="group-card" onClick={() => onGroupClick(group.groupId)}>
            <h3>{group.title || group.groupId}</h3>
            <p>👥 {group.memberCount} members</p>
            {group.username && <p>🔗 @{group.username}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// User Card Component
const UserCard = ({ user, onGroupClick }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Get primary name (first one)
  const primaryName = user.names && Object.keys(user.names).length > 0 ? Object.keys(user.names)[0] : 'No Name';
  const primaryUsername = user.usernames && Object.keys(user.usernames).length > 0 ? Object.keys(user.usernames)[0] : null;
  
  return (
    <div className="user-card">
      <div className="user-header" onClick={() => setExpanded(!expanded)}>
        <h3>
          {primaryName}
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        </h3>
        {primaryUsername && <p>@{primaryUsername}</p>}
      </div>
      
      {expanded && (
        <div className="user-details">
          <p><strong>User ID:</strong> {user.userid}</p>
          <p><strong>Last Updated:</strong> {user.lastUpdated ? new Date(user.lastUpdated).toLocaleString() : 'N/A'}</p>
          
          {/* All Names */}
          {user.names && Object.keys(user.names).length > 0 && (
            <div className="names-history">
              <strong>All Names ({Object.keys(user.names).length}):</strong>
              <ul>
                {Object.entries(user.names).map(([name, details]) => (
                  <li key={name}>
                    {name} 
                    <small>(First: {details.firstSeen ? new Date(details.firstSeen).toLocaleDateString() : 'N/A'})</small>
                    {details.lastSeen && <small> | Last: {new Date(details.lastSeen).toLocaleDateString()}</small>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Groups */}
          {user.groupsList && user.groupsList.length > 0 && (
            <div className="user-groups">
              <strong>Groups ({user.groupsList.length}):</strong>
              <ul>
                {user.groupsList.map(group => (
                  <li key={group.groupId}>
                    <button 
                      className="group-link"
                      onClick={() => onGroupClick(group.groupId)}
                    >
                      {group.title || group.groupId}
                    </button>
                    <small>
                      Joined: {group.firstSeen ? new Date(group.firstSeen).toLocaleDateString() : 'N/A'}
                      {group.lastSeen && ` | Last seen: ${new Date(group.lastSeen).toLocaleDateString()}`}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Group Card Component
const GroupCard = ({ group, onUserClick }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="group-card-detail">
      <div className="group-header" onClick={() => setExpanded(!expanded)}>
        <h3>
          {group.title || group.groupId}
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        </h3>
        <p>👥 {group.memberCount} members</p>
        {group.username && <p>🔗 @{group.username}</p>}
      </div>
      
      {expanded && (
        <div className="group-details">
          <p><strong>Group ID:</strong> {group.groupId}</p>
          
          {/* Members List */}
          <div className="group-members">
            <strong>Members ({group.memberCount}):</strong>
            <ul>
              {group.users && group.users.map(member => (
                <li key={member.userid}>
                  <button 
                    className="user-link"
                    onClick={() => onUserClick(member.userid)}
                  >
                    {member.names && member.names.length > 0 ? member.names.join(', ') : member.userid}
                  </button>
                  <small>
                    Joined: {member.firstSeen ? new Date(member.firstSeen).toLocaleDateString() : 'N/A'}
                    {member.lastSeen && ` | Last seen: ${new Date(member.lastSeen).toLocaleDateString()}`}
                  </small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { allData, lastUpdated, refreshData, loading, error } = useData();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState(null);

  const handleSearch = (query, filterType) => {
    if (!allData) return;
    
    const lowerQuery = query.toLowerCase();
    
    let filteredUsers = [];
    let filteredGroups = [];
    
    if (filterType === 'all' || filterType === 'users') {
      filteredUsers = allData.users.filter(user => 
        (user.names && Object.keys(user.names).some(name => name.toLowerCase().includes(lowerQuery))) ||
        (user.usernames && Object.keys(user.usernames).some(username => username.toLowerCase().includes(lowerQuery))) ||
        (user.userid && user.userid.toString().includes(lowerQuery))
      );
    }
    
    if (filterType === 'all' || filterType === 'groups') {
      filteredGroups = allData.groups.filter(group =>
        (group.title && group.title.toLowerCase().includes(lowerQuery)) ||
        (group.groupId && group.groupId.toLowerCase().includes(lowerQuery)) ||
        (group.username && group.username.toLowerCase().includes(lowerQuery))
      );
    }
    
    setSearchResults({ users: filteredUsers, groups: filteredGroups, query, filterType });
  };

  const handleGroupClick = (groupId) => {
    navigate(`/data/group/${groupId}`);
  };

  const handleUserClick = (userId) => {
    navigate(`/data/user/${userId}`);
  };

  const clearSearch = () => {
    setSearchResults(null);
  };

  // Show initial state (no data loaded yet)
  if (!allData && !loading) {
    return (
      <div className="dashboard">
        <div className="header">
          <h1>📊 Telegram Data Dashboard</h1>
          <div className="header-info">
            <button onClick={refreshData} className="refresh-btn">
              🔄 Load Data
            </button>
          </div>
        </div>
        <div className="no-data">
          <p>No data loaded. Click "Load Data" to fetch the latest information.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="header">
          <h1>📊 Telegram Data Dashboard</h1>
          <div className="header-info">
            <button onClick={refreshData} className="refresh-btn" disabled>
              🔄 Loading...
            </button>
          </div>
        </div>
        <div className="loading">Loading data...</div>
      </div>
    );
  }

  if (error && !allData) {
    return (
      <div className="dashboard">
        <div className="header">
          <h1>📊 Telegram Data Dashboard</h1>
          <div className="header-info">
            <button onClick={refreshData} className="refresh-btn">
              🔄 Retry
            </button>
          </div>
        </div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const displayData = searchResults || {
    users: allData.users,
    groups: allData.groups,
    query: '',
    filterType: 'all'
  };

  return (
    <div className="dashboard">
      <div className="header">
        <h1>📊 Telegram Data Dashboard</h1>
        <div className="header-info">
          {lastUpdated && <p>Last updated: {new Date(lastUpdated).toLocaleString()}</p>}
          <button onClick={refreshData} className="refresh-btn" disabled={loading}>
            🔄 {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>
      
      <SearchBar onSearch={handleSearch} />
      
      {searchResults && searchResults.query && (
        <div className="search-info">
          <p>Search results for: "{searchResults.query}" ({searchResults.filterType})</p>
          <button onClick={clearSearch}>Clear Search</button>
        </div>
      )}
      
      <div className="stats">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p>{allData.summary.totalUsers}</p>
        </div>
        <div className="stat-card">
          <h3>Total Groups</h3>
          <p>{allData.summary.totalGroups}</p>
        </div>
        <div className="stat-card">
          <h3>Avg Groups/User</h3>
          <p>{allData.summary.averageGroupsPerUser}</p>
        </div>
        <div className="stat-card">
          <h3>Avg Members/Group</h3>
          <p>{allData.summary.averageMembersPerGroup}</p>
        </div>
      </div>
      
      {allData.groups && allData.groups.length > 0 && (
        <TopGroups groups={allData.groups} onGroupClick={handleGroupClick} />
      )}
      
      <div className="sections">
        <div className="users-section">
          <h2>👥 All Users ({displayData.users.length})</h2>
          <div className="users-list">
            {displayData.users.map(user => (
              <UserCard key={user.userid} user={user} onGroupClick={handleGroupClick} />
            ))}
          </div>
        </div>
        
        <div className="groups-section">
          <h2>💬 All Groups ({displayData.groups.length})</h2>
          <div className="groups-list">
            {displayData.groups.map(group => (
              <GroupCard key={group.groupId} group={group} onUserClick={handleUserClick} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// User Detail Page
const UserDetail = () => {
  const { userId } = useParams();
  const { allData } = useData();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (allData && allData.users) {
      const foundUser = allData.users.find(u => u.userid === parseInt(userId) || u.userid === userId);
      setUser(foundUser);
    }
  }, [userId, allData]);

  if (!allData) {
    return (
      <div className="detail-page">
        <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
        <div className="loading">Please load data first from dashboard</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="detail-page">
        <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
        <div className="error">User not found</div>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
      
      <div className="user-detail">
        <h1>User Details</h1>
        <div className="detail-card">
          <p><strong>User ID:</strong> {user.userid}</p>
          <p><strong>Last Updated:</strong> {user.lastUpdated ? new Date(user.lastUpdated).toLocaleString() : 'N/A'}</p>
          
          <h3>Names</h3>
          {user.names && Object.entries(user.names).map(([name, details]) => (
            <div key={name} className="name-entry">
              <p><strong>{name}</strong></p>
              <small>First seen: {details.firstSeen ? new Date(details.firstSeen).toLocaleString() : 'N/A'}</small>
              {details.lastSeen && <small> | Last seen: {new Date(details.lastSeen).toLocaleString()}</small>}
            </div>
          ))}
          
          <h3>Usernames</h3>
          {user.usernames && Object.entries(user.usernames).map(([username, details]) => (
            <div key={username} className="username-entry">
              <p><strong>@{username}</strong></p>
              <small>First seen: {details.firstSeen ? new Date(details.firstSeen).toLocaleString() : 'N/A'}</small>
              {details.lastSeen && <small> | Last seen: {new Date(details.lastSeen).toLocaleString()}</small>}
            </div>
          ))}
          
          <h3>Groups ({user.groupsList ? user.groupsList.length : 0})</h3>
          {user.groupsList && user.groupsList.map(group => (
            <div key={group.groupId} className="group-entry">
              <button 
                className="group-link"
                onClick={() => navigate(`/data/group/${group.groupId}`)}
              >
                {group.title || group.groupId}
              </button>
              <small>
                Joined: {group.firstSeen ? new Date(group.firstSeen).toLocaleString() : 'N/A'}
                {group.lastSeen && ` | Last seen: ${new Date(group.lastSeen).toLocaleString()}`}
              </small>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Group Detail Page
const GroupDetail = () => {
  const { groupId } = useParams();
  const { allData } = useData();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);

  useEffect(() => {
    if (allData && allData.groups) {
      const foundGroup = allData.groups.find(g => g.groupId === groupId);
      setGroup(foundGroup);
    }
  }, [groupId, allData]);

  if (!allData) {
    return (
      <div className="detail-page">
        <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
        <div className="loading">Please load data first from dashboard</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="detail-page">
        <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
        <div className="error">Group not found</div>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <button onClick={() => navigate('/data')} className="back-btn">← Back to Dashboard</button>
      
      <div className="group-detail">
        <h1>Group Details</h1>
        <div className="detail-card">
          <p><strong>Group ID:</strong> {group.groupId}</p>
          <p><strong>Title:</strong> {group.title || 'N/A'}</p>
          {group.username && <p><strong>Username:</strong> @{group.username}</p>}
          <p><strong>Total Members:</strong> {group.memberCount}</p>
          
          <h3>Members</h3>
          <div className="members-list">
            {group.users && group.users.map(member => (
              <div key={member.userid} className="member-entry">
                <button 
                  className="user-link"
                  onClick={() => navigate(`/data/user/${member.userid}`)}
                >
                  {member.names && member.names.length > 0 ? member.names.join(', ') : member.userid}
                </button>
                <small>
                  User ID: {member.userid} | 
                  Joined: {member.firstSeen ? new Date(member.firstSeen).toLocaleString() : 'N/A'}
                  {member.lastSeen && ` | Last seen: ${new Date(member.lastSeen).toLocaleString()}`}
                </small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add CSS styles
const styles = `
  .dashboard {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  .header-info {
    display: flex;
    gap: 15px;
    align-items: center;
  }
  .refresh-btn {
    padding: 8px 16px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  .refresh-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  .search-bar {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
  }
  .search-bar input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
  }
  .search-bar select {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  .search-bar button {
    padding: 10px 20px;
    background-color: #2196F3;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
  }
  .stat-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 20px;
    border-radius: 10px;
    text-align: center;
  }
  .stat-card h3 {
    margin: 0 0 10px 0;
    font-size: 14px;
    opacity: 0.9;
  }
  .stat-card p {
    margin: 0;
    font-size: 28px;
    font-weight: bold;
  }
  .top-groups {
    margin-bottom: 30px;
  }
  .groups-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
    margin-top: 15px;
  }
  .group-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .group-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  .users-list, .groups-list {
    max-height: 600px;
    overflow-y: auto;
  }
  .user-card, .group-card-detail {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 15px;
    overflow: hidden;
  }
  .user-header, .group-header {
    padding: 15px;
    cursor: pointer;
    background: #f9f9f9;
    transition: background 0.2s;
  }
  .user-header:hover, .group-header:hover {
    background: #f0f0f0;
  }
  .user-header h3, .group-header h3 {
    margin: 0 0 5px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .expand-icon {
    font-size: 12px;
    color: #999;
  }
  .user-details, .group-details {
    padding: 15px;
    border-top: 1px solid #e0e0e0;
  }
  .group-link, .user-link {
    background: none;
    border: none;
    color: #2196F3;
    cursor: pointer;
    text-decoration: underline;
    font-size: 14px;
    padding: 0;
    margin-right: 10px;
  }
  .detail-page {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
  }
  .back-btn {
    padding: 10px 20px;
    background-color: #666;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 20px;
  }
  .detail-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
  }
  .loading, .error, .no-data {
    text-align: center;
    padding: 50px;
    font-size: 18px;
  }
  .error {
    color: #f44336;
  }
  .search-info {
    background: #e3f2fd;
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  @media (max-width: 768px) {
    .sections {
      grid-template-columns: 1fr;
    }
    .search-bar {
      flex-direction: column;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Main Data Component
const Data = () => {
  return (
    <DataProvider>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/user/:userId" element={<UserDetail />} />
        <Route path="/group/:groupId" element={<GroupDetail />} />
      </Routes>
    </DataProvider>
  );
};

export default Data;
